/**
 * Optimization Metrics Calculator - Pure functions for optimization metrics
 *
 * This module provides Level 5 pure functions for calculating optimization-related
 * metrics including DRF surplus, utilization improvements, and revenue calculations.
 */

/**
 * Calculates DRF surplus (ending balance minus starting balance)
 * @param endingBalance - DRF ending balance
 * @param startingBalance - DRF starting balance
 * @returns DRF surplus amount
 * @pure Level 5 calculation - no side effects
 */
export function calculateDrfSurplus(endingBalance: number, startingBalance: number): number {
  return endingBalance - startingBalance;
}

/**
 * Calculates improvement between two values
 * @param newValue - New/improved value
 * @param originalValue - Original/baseline value
 * @returns Improvement amount (can be negative)
 * @pure Level 5 calculation - no side effects
 */
export function calculateImprovement(newValue: number, originalValue: number): number {
  return newValue - originalValue;
}

/**
 * Calculates improvement percentage
 * @param newValue - New/improved value
 * @param originalValue - Original/baseline value
 * @returns Improvement as percentage points
 * @pure Level 5 calculation - no side effects
 */
export function calculateImprovementPercentage(newValue: number, originalValue: number): number {
  return newValue - originalValue;
}

/**
 * Calculates utilization percentage (0-100 scale)
 * @param utilizationRatio - Utilization ratio (0-1 scale)
 * @returns Utilization as percentage
 * @pure Level 5 calculation - no side effects
 */
export function calculateUtilizationPercentage(utilizationRatio: number): number {
  return utilizationRatio * 100;
}

/**
 * Calculates revenue from millage rate and taxable value
 * @param millageRate - Millage rate (in mills, e.g., 2.5 for 2.5 mills)
 * @param taxableValue - Taxable value in dollars
 * @returns Total revenue generated
 * @pure Level 5 calculation - no side effects
 */
export function calculateMillageRevenue(millageRate: number, taxableValue: number): number {
  return (millageRate / 1000) * taxableValue;
}

/**
 * Calculates millage rate from revenue and taxable value
 * @param revenue - Total revenue
 * @param taxableValue - Taxable value in dollars
 * @returns Millage rate in mills
 * @pure Level 5 calculation - no side effects
 */
export function calculateMillageFromRevenue(revenue: number, taxableValue: number): number {
  if (taxableValue <= 0) return 0;
  return (revenue / taxableValue) * 1000;
}

/**
 * Calculates average value
 * @param total - Total sum
 * @param count - Number of items
 * @returns Average value
 * @pure Level 5 calculation - no side effects
 */
export function calculateAverage(total: number, count: number): number {
  if (count <= 0) return 0;
  return total / count;
}

/**
 * Calculates target reduction amount
 * @param excessAmount - Excess amount available
 * @param reductionTarget - Target reduction ratio (0-1 scale)
 * @returns Target reduction amount
 * @pure Level 5 calculation - no side effects
 */
export function calculateTargetReduction(excessAmount: number, reductionTarget: number): number {
  return excessAmount * reductionTarget;
}

/**
 * Calculates actual amount to pull/adjust
 * @param maxAvailable - Maximum available amount
 * @param targetAmount - Target amount desired
 * @returns Actual amount (minimum of max and target)
 * @pure Level 5 calculation - no side effects
 */
export function calculateActualAdjustment(maxAvailable: number, targetAmount: number): number {
  return Math.min(maxAvailable, targetAmount);
}

/**
 * Calculates remaining amount after pull
 * @param originalAmount - Original amount
 * @param pulledAmount - Amount pulled/removed
 * @returns Remaining amount
 * @pure Level 5 calculation - no side effects
 */
export function calculateRemainingAfterPull(originalAmount: number, pulledAmount: number): number {
  return originalAmount - pulledAmount;
}

/**
 * Calculates the absolute imbalance between two values
 * @param value1 - First value
 * @param value2 - Second value
 * @returns Absolute difference
 * @pure Level 5 calculation - no side effects
 */
export function calculateImbalance(value1: number, value2: number): number {
  return Math.abs(value1 - value2);
}

/**
 * Calculates viability ratio
 * @param viableCount - Number of viable items
 * @param totalCount - Total number of items
 * @returns Viability ratio (0-1 scale)
 * @pure Level 5 calculation - no side effects
 */
export function calculateViabilityRatio(viableCount: number, totalCount: number): number {
  if (totalCount <= 0) return 0;
  return viableCount / totalCount;
}
